import {
  PutSecretValueCommand,
  SecretsManagerClient,
} from "@aws-sdk/client-secrets-manager";

import { HttpClient, robotoHeaders, PaginatedResponse } from "@/shared/http";

import { GetSecretAccessCredsResponse, SecretRecord } from "./SecretRecord";

interface Options {
  abortSignal: AbortSignal;
}

export class SecretService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  private async getCredentialsForSecret(
    secretName: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<GetSecretAccessCredsResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/secrets/name/${secretName}/creds`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });

    return response.json<GetSecretAccessCredsResponse>();
  }

  private async initializeSecret(
    secretName: string,
    secretValue: string,
    orgId: string,
  ): Promise<void> {
    const creds = await this.getCredentialsForSecret(secretName, orgId);

    const config = {
      region: creds.creds.region,
      credentials: {
        accessKeyId: creds.creds.access_key_id,
        secretAccessKey: creds.creds.secret_access_key,
        sessionToken: creds.creds.session_token,
      },
    };
    const client = new SecretsManagerClient(config);

    const formattedName = `org_secrets/${orgId}/${secretName}`;

    const input = {
      SecretId: formattedName,
      SecretString: secretValue,
    };
    await client.send(new PutSecretValueCommand(input));
  }

  public async createSecret(
    name: string,
    value: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<SecretRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/secrets`);

    const body = {
      name,
    };

    const response = await this.#httpClient.post(requestUrl, {
      body: JSON.stringify(body),
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });

    const secretRecord = await response.json<SecretRecord>();

    try {
      await this.initializeSecret(name, value, orgId);
    } catch (error) {
      // Rollback: delete the DB record since AWS initialization failed
      await this.deleteSecretByName(name, orgId).catch(() => {});
      throw error;
    }

    return secretRecord;
  }

  public async getSecretsForOrg(
    orgId: string,
    pageSize: number,
    nextToken?: string,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<SecretRecord>> {
    const searchParams = new URLSearchParams();
    searchParams.append("page_size", pageSize.toString());
    if (nextToken) {
      searchParams.append("page_token", nextToken);
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/secrets`,
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });

    return response.json<PaginatedResponse<SecretRecord>>();
  }

  public async getSecretByName(
    secretName: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<SecretRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/secrets/name/${secretName}`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });

    return response.json<SecretRecord>();
  }

  public async deleteSecretByName(
    secretName: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/secrets/name/${secretName}`,
    );

    const response = await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });

    return response.throwIfError();
  }
}
